import { useState, useEffect } from 'react';
import { InputTextCustom, Modal, formatDate } from 'medere-ui-framework';
import './PendingRecipeTab.scss';
import CollapsibleCard from '../../../../components/Collapsible/CollapsibleCard';
import { Button } from 'primereact/button';
import { Medicine, PatientRecordDTO, Professional, RequestedPrescriptions, UserAllowedToPrescript } from '../../../../interface/interface';
import { getPatientData, loadRpDetailsForRequestedPrescription, loadRequestedPrescriptions, rejectPrescriptionRequest } from '../../../../servicesHooks/DigitalRP/useDigitalRPService';
import { addLineBreaksAndStippling } from '../../Components/GeneralRecipeViewCard';

const PendingRecipeTab = (params: { allProfileDataFilled: boolean, setMedications: (medicine: Medicine[] | null) => void, setSelectedPatient: (patient: PatientRecordDTO | null) => void, pendingRecipes: RequestedPrescriptions[] | null | undefined, setPendingRecipes: (recipe: RequestedPrescriptions[] | null | undefined) => void, setRequestPrescription: (recipe: number) => void, professional: Professional, patientInput: string, setGoToMedicationStep: (gtms: boolean) => void, handleToggleCard: (r: RequestedPrescriptions) => void, openCard: number | null, setOpenCard: (soc: number) => void, secretary: UserAllowedToPrescript | null }) => {
    const { allProfileDataFilled, setMedications, setSelectedPatient, pendingRecipes, setPendingRecipes, setRequestPrescription, professional, patientInput, setGoToMedicationStep, handleToggleCard, openCard, secretary } = params
    const [discardRecipeModal, setDiscardRecipeModal] = useState(false);
    const [indexToRemove, setIndexToRemove] = useState<number>();
    const [cancellationReason, setCancellationReason] = useState<string>("");
    const [errorCancellationReason, setErrorCancellationReason] = useState(false);

    const [paddingTop, setPaddingTop] = useState('55%');
    const [isMobile, setisMobile] = useState(window.innerWidth < 700)

    useEffect(() => {
        const handleResize = () => {
            setisMobile(window.innerWidth < 700);
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        const loadRequestedRecipes = async () => {
            const requestedRecipes: RequestedPrescriptions[] | null = await loadRequestedPrescriptions(professional?.professionaid, patientInput)
                .then((res) => {
                    return res
                })
                .catch((error) => {
                    console.error('Error: ', error);
                    return [];
                });
            setPendingRecipes(requestedRecipes);
        };

        if (professional.professionaid > 0)
            loadRequestedRecipes();

    }, [patientInput]);

    useEffect(() => {
        const computePaddingTop = () => { //padding para las modales
            const minWidth = 430;
            const maxWidth = 1024;
            const minPadding = 20;
            const maxPadding = 50;
            const windowWidth = window.innerWidth;

            if (windowWidth <= minWidth) {
                setPaddingTop(`${maxPadding}%`);
            } else if (windowWidth >= maxWidth) {
                setPaddingTop(`${minPadding}%`);
            } else {
                const padding = maxPadding - ((windowWidth - minWidth) / (maxWidth - minWidth)) * (maxPadding - minPadding);
                setPaddingTop(`${padding}%`);
            }
        };

        computePaddingTop();
        window.addEventListener('resize', computePaddingTop);

        return () => {
            window.removeEventListener('resize', computePaddingTop);
        };
    }, []);

    useEffect(() => {
        if (cancellationReason && cancellationReason.trim() != "")
            setErrorCancellationReason(false)
    }, [cancellationReason])

    useEffect(() => {
        if (!discardRecipeModal) {
            setCancellationReason('')
            setErrorCancellationReason(false)
        }
    }, [discardRecipeModal])

    const loadPatientInformation = async (patientid: number) => {
        const patient: PatientRecordDTO | null = await getPatientData(patientid)
            .then((res) => {
                return res
            })
        setSelectedPatient(patient);
    };

    const handleOpenDiscardRecipeModal = (index: number) => {
        setIndexToRemove(index)
        setDiscardRecipeModal(true)
    };

    function handleCancellationReasonChange(event: any) {
        setCancellationReason(event.target.value);
    }

    const handleRemoveRecipe = () => {
        if (!cancellationReason || cancellationReason.trim() == '') {
            setErrorCancellationReason(true)
            return
        }

        const pendingRecipeToRemove: RequestedPrescriptions | undefined = pendingRecipes?.filter((_, i) => i === indexToRemove)[0];
        rejectPrescriptionRequest({ rejectiondescription: cancellationReason, digitalrp: pendingRecipeToRemove?.prescriptionrequest ?? 0 }) //notifica al paciente que se descartó su receta

        const updatedPendingRecipes: RequestedPrescriptions[] | undefined = pendingRecipes?.filter((_, i) => i !== indexToRemove);
        setPendingRecipes(updatedPendingRecipes);
        setDiscardRecipeModal(false)
    };

    const handleGoToMedicationStep = (recipe: RequestedPrescriptions) => {

        loadPatientInformation(recipe.patientid);

        const loadMedicineInformation = async () => {
            const medicationList: Medicine[] | null = await loadRpDetailsForRequestedPrescription(recipe.prescriptionrequest)
                .then((res) => {
                    return res
                })
                .catch((error) => {
                    console.error('Error: ', error);
                    return [];
                });
            setMedications(medicationList);
        };

        loadMedicineInformation()

        setRequestPrescription(recipe.prescriptionrequest)

        setGoToMedicationStep(true)

    };

    const showPendingRecipes = () => (
        pendingRecipes?.map((recipe, index) => secretary ? renderPendingRecipeContentSecretary(recipe, index) : renderPendingRecipeContentProfessional(recipe, index))
    );

    const renderPendingRecipeContentSecretary = (recipe: RequestedPrescriptions, index: number) => (
        <CollapsibleCard
            key={index}
            title={<label style={{ fontWeight: 600, fontSize: '16px', color: 'black' }}>{recipe.patientfullname}</label>}
            subtitle={formatDate(recipe.requesteddate) + " " + recipe.professional + " prescripción: " + recipe.prescriptionrequest}
            isOpen={openCard === recipe.prescriptionrequest} //para que se cierren todas menos la que abrí
            onClick={() => { handleToggleCard(recipe) }}
        >
            <div>
                {addLineBreaksAndStippling(recipe.medicinesfulldescription, false)}
                <div style={{ marginTop: '3px', display: 'flex', flexDirection: 'column' }}>
                    <Button
                        label={"Recetar"}
                        disabled={!allProfileDataFilled}
                        className="recetar-button"
                        onClick={() => handleGoToMedicationStep(recipe)}
                    />
                    <Button
                        label={"Descartar"}
                        disabled={!allProfileDataFilled}
                        className="descartar-button"
                        onClick={() => handleOpenDiscardRecipeModal(index)}
                    />
                </div>
            </div>
        </CollapsibleCard>
    );

    const renderPendingRecipeContentProfessional = (recipe: RequestedPrescriptions, index: number) => (
        <CollapsibleCard
            key={index}
            title={<label style={{ fontWeight: 600, fontSize: '16px', color: 'black' }}>{recipe.patientfullname}</label>}
            subtitle={formatDate(recipe.requesteddate)}
        >
            <div>
                {addLineBreaksAndStippling(recipe.medicinesfulldescription, false)}
                <div style={{ marginTop: '3px', display: 'flex', flexDirection: 'column' }}>
                    <Button
                        label={"Recetar"}
                        disabled={!allProfileDataFilled}
                        className="recetar-button"
                        onClick={() => handleGoToMedicationStep(recipe)}
                    />
                    <Button
                        label={"Descartar"}
                        disabled={!allProfileDataFilled}
                        className="descartar-button"
                        onClick={() => handleOpenDiscardRecipeModal(index)}
                    />
                </div>
            </div>
        </CollapsibleCard>
    );

    if (pendingRecipes?.length === 0) {
        return <div style={{ fontFamily: 'sans-serif', fontStyle: 'italic', fontSize: '14px', color: '#838383' }}>No hay solicitudes pendientes.</div>;
    }

    return (
        <div className="pending-recipe-tab">
            <div style={{ marginBottom: '15px' }}>
                Existen <label style={{ color: '#A53E2B' }}>{pendingRecipes?.length} recetas pendientes.</label>
            </div>
            {isMobile ?
                showPendingRecipes()
                :
                <div style={{
                    display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px',
                    alignItems: 'start' //hace falta para que no se habran los del costado
                }}>
                    {showPendingRecipes()}
                </div>
            }

            <Modal
                visible={discardRecipeModal}
                setVisible={setDiscardRecipeModal}
                header={<div style={{ display: 'flex', flexDirection: 'column', textAlign: 'center' }}><i className="pi pi-exclamation-circle" style={{ fontSize: '48px', color: '#A53E2B' }}></i>
                    <label style={{ marginTop: '10px' }}><strong>Descartar receta pendiente</strong></label></div>}
                headerStyle={{ fontSize: '12px', textAlign: 'center', paddingTop: paddingTop }}
                closable={false}
                className='h-full'
            >
                <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
                    <label style={{ marginBottom: '1.5rem' }}>Estás por eliminar un pedido pendiente de receta que hizo un paciente. No volverás a tener acceso a ella. El mensaje será visto por el paciente.</label>

                    <div style={{ marginBottom: '15px', textAlign: 'left' }}>
                        <label>Motivo de descarte: </label>
                        <InputTextCustom
                            className={errorCancellationReason ? '' : 'border2'}
                            value={cancellationReason}
                            onChange={handleCancellationReasonChange}
                            error={errorCancellationReason}
                            caption={errorCancellationReason ? "Debes detallar el motivo" : ""}
                        />
                    </div>
                    <Button
                        label="Si, descartar"
                        onClick={handleRemoveRecipe}
                        style={{ marginBottom: '15px', backgroundColor: '#14C7BC', border: 'none' }}
                    />
                    <Button
                        label="Volver al pedido de la receta"
                        onClick={() => setDiscardRecipeModal(false)}
                        style={{ backgroundColor: '#FFFFFF', color: '#545454', borderColor: '#3CB8BA' }}
                    />
                </div>
            </Modal>

        </div>
    );
};

export default PendingRecipeTab;
